import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Alert,
  Autocomplete,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppDispatch, useAppSelector } from '../../../store/hooks.ts';
import {
  selectDoraFinancialEntities,
  setDoraFinancialEntities,
} from '../store/financialEntity/financialEntitySlice.ts';
import {
  addDoraICTThirdPartyProvider,
  removeDoraICTThirdPartyProvider,
  selectDoraICTThirdPartyProviders,
  setDoraICTThirdPartyProviders,
  updateDoraICTThirdPartyProvider as updateDoraICTThirdPartyProviderState,
} from '../store/ictThirdPartyProvider/ictThirdPartyProviderSlice.ts';
import {
  createDoraICTThirdPartyProvider,
  deleteDoraICTThirdPartyProvider,
  listDoraFinancialEntities,
  listDoraICTThirdPartyProviders,
  updateDoraICTThirdPartyProvider,
} from '../../../api.ts';
import { DoraICTThirdPartyProvider } from '../models/doraICTThirdPartyProvider.ts';
import { selectCurrentOrganization } from '../../../store/organization/organizationSlice.ts';
import { useExcelTableState } from '../../../hooks/useExcelTableState.ts';
import {
  parsePasteData,
  calculateNextCell,
  applyPastedData,
  getRowBackgroundColor,
  getRowHoverColor,
} from '../../../utils/excelTableUtils.ts';
import { COUNTRY_OPTIONS } from '../constants/countries.ts';
import { CURRENCY_OPTIONS } from '../constants/currencies.ts';
import {
  PERSON_TYPE_OPTIONS,
  PROVIDER_ID_TYPE_BASE_OPTIONS,
  NATIONAL_ID_TYPES,
  isValidLEI,
} from '../constants/providerIdTypes.ts';

interface EditableRow {
  id?: number;
  description: string;
  // B_05.01 Mandatory Fields
  provider_id_code: string;
  provider_id_type: string;
  additional_id_code: string;
  additional_id_type: string;
  legal_name: string;
  name_latin: string;
  person_type: string;
  headquarters_country: string;
  // B_05.01 Conditional Fields
  currency_code: string;
  annual_expense: string; // String for easier editing, converted to number on save
  parent_id_code: string;
  parent_id_type: string;
  // Relationships
  financial_entity_id: number | '';
  isNew?: boolean;
  touched?: boolean;
}

export const ICTThirdPartyProvidersPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const financialEntities = useAppSelector(selectDoraFinancialEntities);
  const providers = useAppSelector(selectDoraICTThirdPartyProviders);
  const currentOrganization = useAppSelector(selectCurrentOrganization);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editableRows, setEditableRows] = useState<EditableRow[]>([]);

  const {
    focusedRowIndex,
    editingCell,
    setEditingCell,
    handleRowFocus: baseHandleRowFocus,
    handleRowBlur: baseHandleRowBlur,
    isSaving,
    markSaving,
    markSaved,
  } = useExcelTableState<EditableRow>();

  // Convert providers to editable rows and always add an empty row at the end
  const rows = useMemo(() => {
    const existingRows: EditableRow[] = providers.map(p => ({
      id: p.id,
      description: p.description || '',
      provider_id_code: p.provider_id_code,
      provider_id_type: p.provider_id_type,
      additional_id_code: p.additional_id_code,
      additional_id_type: p.additional_id_type,
      legal_name: p.legal_name,
      name_latin: p.name_latin,
      person_type: p.person_type,
      headquarters_country: p.headquarters_country,
      currency_code: p.currency_code || '',
      annual_expense: p.annual_expense !== null && p.annual_expense !== undefined ? String(p.annual_expense) : '',
      parent_id_code: p.parent_id_code || '',
      parent_id_type: p.parent_id_type || '',
      financial_entity_id: p.financial_entity_id,
    }));

    // Add empty row for new entries
    const emptyRow: EditableRow = {
      description: '',
      provider_id_code: '',
      provider_id_type: 'LEI',
      additional_id_code: '',
      additional_id_type: '',
      legal_name: '',
      name_latin: '',
      person_type: PERSON_TYPE_OPTIONS[0],
      headquarters_country: '',
      currency_code: '',
      annual_expense: '',
      parent_id_code: '',
      parent_id_type: '',
      financial_entity_id: financialEntities[0]?.id ?? '',
      isNew: true,
    };

    return [...existingRows, emptyRow];
  }, [providers, financialEntities]);

  useEffect(() => {
    setEditableRows(rows);
  }, [rows]);

  useEffect(() => {
    const load = async () => {
      if (!currentOrganization) return;
      setLoading(true);
      setError(null);
      try {
        const [fes, list] = await Promise.all([
          listDoraFinancialEntities(),
          listDoraICTThirdPartyProviders(),
        ]);
        dispatch(setDoraFinancialEntities(fes));
        dispatch(setDoraICTThirdPartyProviders(list));
      } catch (err: any) {
        console.error('Failed to load ICT third-party providers', err);
        setError(err?.response?.data?.detail || 'Failed to load ICT third-party providers');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentOrganization, dispatch]);

  const updateRow = (index: number, field: keyof EditableRow, value: any) => {
    setEditableRows(prev => {
      const updated = [...prev];
      const row = updated[index];
      // Mark new rows as touched when first edited
      updated[index] = {
        ...row,
        [field]: value,
        ...(row.isNew && !row.touched ? { touched: true } : {})
      };
      return updated;
    });
  };

  // Helper to determine if a required field is empty
  const isRequiredFieldEmpty = (row: EditableRow, field: keyof EditableRow): boolean => {
    const requiredFields: (keyof EditableRow)[] = [
      'provider_id_code',
      'provider_id_type',
      'additional_id_code',
      'additional_id_type',
      'legal_name',
      'name_latin',
      'person_type',
      'headquarters_country',
      'financial_entity_id',
    ];

    // Conditional required fields
    if (field === 'currency_code' && row.annual_expense.trim()) {
      requiredFields.push('currency_code');
    }
    if (field === 'parent_id_type' && row.parent_id_code.trim()) {
      requiredFields.push('parent_id_type');
    }
    if (field === 'parent_id_code' && row.parent_id_type.trim()) {
      requiredFields.push('parent_id_code');
    }

    if (!requiredFields.includes(field)) return false;

    const value = row[field];
    return value === '' || value === null || value === undefined;
  };

  // Helper to get cell background color with subtle amber shine for required empty fields
  const getCellBackgroundColor = (row: EditableRow, field: keyof EditableRow): string => {
    // Don't highlight untouched new rows
    if (row.isNew && !row.touched) {
      return 'transparent';
    }

    if (isRequiredFieldEmpty(row, field)) {
      // Brighter, more fancy amber/gold shine with subtle gradient effect
      return 'linear-gradient(135deg, rgba(255, 193, 7, 0.12), rgba(255, 160, 0, 0.08))';
    }
    return 'transparent';
  };

  const fieldOrder: (keyof EditableRow)[] = [
    'provider_id_code',
    'provider_id_type',
    'legal_name',
    'name_latin',
    'person_type',
    'headquarters_country',
    'additional_id_code',
    'additional_id_type',
    'annual_expense',
    'currency_code',
    'parent_id_code',
    'parent_id_type',
    'description',
    'financial_entity_id',
  ];

  const handleRowFocus = (index: number) => {
    baseHandleRowFocus(index, editableRows, saveRow);
  };

  const handleRowBlur = () => {
    baseHandleRowBlur(editableRows, saveRow);
  };

  const handleTabNavigation = (e: React.KeyboardEvent, currentRow: number, currentField: keyof EditableRow) => {
    if (e.key !== 'Tab') return;

    e.preventDefault();

    const currentFieldIndex = fieldOrder.indexOf(currentField);
    const nextCell = calculateNextCell(
      currentRow,
      currentFieldIndex,
      editableRows.length,
      fieldOrder.length,
      e.shiftKey
    );

    if (nextCell) {
      if (nextCell.row !== currentRow) {
        handleRowFocus(nextCell.row);
      }
      setEditingCell({ row: nextCell.row, field: fieldOrder[nextCell.fieldIndex] });
    }
  };

  const saveRow = useCallback(async (row: EditableRow, index: number) => {
    const rowKey = row.id ?? `new-${index}`;

    // Prevent duplicate saves
    if (isSaving(rowKey)) return;

    // Validate required fields
    const requiredFieldsFilled =
      row.provider_id_code.trim() &&
      row.provider_id_type.trim() &&
      row.additional_id_code.trim() &&
      row.additional_id_type.trim() &&
      row.legal_name.trim() &&
      row.name_latin.trim() &&
      row.person_type.trim() &&
      row.headquarters_country.trim() &&
      row.financial_entity_id;

    // Validate conditional fields
    const annualExpenseProvided = row.annual_expense.trim();
    const currencyCodeProvided = row.currency_code.trim();
    if (annualExpenseProvided && !currencyCodeProvided) {
      setError('Currency code is required when annual expense is provided');
      return;
    }

    const parentCodeProvided = row.parent_id_code.trim();
    const parentTypeProvided = row.parent_id_type.trim();
    if ((parentCodeProvided && !parentTypeProvided) || (!parentCodeProvided && parentTypeProvided)) {
      setError('Both parent ID code and type must be provided together');
      return;
    }

    // Validate LEI format if type is LEI
    if (row.provider_id_type === 'LEI' && row.provider_id_code.trim() && !isValidLEI(row.provider_id_code.trim().toUpperCase())) {
      setError('LEI must be exactly 20 alphanumeric characters');
      return;
    }

    try {
      if (row.isNew) {
        // Create new provider only if required fields are filled
        if (requiredFieldsFilled) {
          markSaving(rowKey);
          const payload = {
            description: row.description || null,
            provider_id_code: row.provider_id_code.trim(),
            provider_id_type: row.provider_id_type,
            additional_id_code: row.additional_id_code.trim(),
            additional_id_type: row.additional_id_type,
            legal_name: row.legal_name.trim(),
            name_latin: row.name_latin.trim(),
            person_type: row.person_type,
            headquarters_country: row.headquarters_country,
            currency_code: row.currency_code.trim() || null,
            annual_expense: row.annual_expense.trim() ? parseFloat(row.annual_expense) : null,
            parent_id_code: row.parent_id_code.trim() || null,
            parent_id_type: row.parent_id_type.trim() || null,
            financial_entity_id: Number(row.financial_entity_id),
          };
          const created = await createDoraICTThirdPartyProvider(payload);
          dispatch(addDoraICTThirdPartyProvider(created));
        }
      } else if (row.id) {
        // Update existing provider
        if (requiredFieldsFilled) {
          markSaving(rowKey);
          const payload = {
            description: row.description || null,
            provider_id_code: row.provider_id_code.trim(),
            provider_id_type: row.provider_id_type,
            additional_id_code: row.additional_id_code.trim(),
            additional_id_type: row.additional_id_type,
            legal_name: row.legal_name.trim(),
            name_latin: row.name_latin.trim(),
            person_type: row.person_type,
            headquarters_country: row.headquarters_country,
            currency_code: row.currency_code.trim() || null,
            annual_expense: row.annual_expense.trim() ? parseFloat(row.annual_expense) : null,
            parent_id_code: row.parent_id_code.trim() || null,
            parent_id_type: row.parent_id_type.trim() || null,
            financial_entity_id: Number(row.financial_entity_id),
          };
          const updated = await updateDoraICTThirdPartyProvider(row.id, payload);
          dispatch(updateDoraICTThirdPartyProviderState(updated));
        }
      }
    } catch (err: any) {
      console.error('Failed to save provider', err);
      setError(err?.response?.data?.detail || 'Failed to save provider');
    } finally {
      markSaved(rowKey);
    }
  }, [dispatch, isSaving, markSaving, markSaved]);

  const handleDelete = async (id?: number) => {
    if (!id) return;
    if (!window.confirm('Delete this provider?')) return;
    setError(null);
    try {
      await deleteDoraICTThirdPartyProvider(id);
      dispatch(removeDoraICTThirdPartyProvider(id));
    } catch (err: any) {
      console.error('Failed to delete provider', err);
      setError(err?.response?.data?.detail || 'Failed to delete provider');
    }
  };

  const handlePaste = useCallback((e: React.ClipboardEvent, startRowIndex: number, startField: keyof EditableRow) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text');
    const startFieldIndex = fieldOrder.indexOf(startField);

    // Parse the pasted data
    const parsedRows = parsePasteData(pasteData);

    // Normalize parsed data: treat visual "empty" representations as actual empty strings
    const normalizedRows = parsedRows.map(row =>
      row.map(cell => {
        const trimmed = cell.trim();
        // Treat various dash characters and common empty representations as empty
        if (trimmed === '—' || trimmed === '-' || trimmed === '–' || trimmed === '−' || trimmed === 'N/A' || trimmed === 'n/a') {
          return '';
        }
        return cell;
      })
    );

    // Create a map of related entities for foreign key matching
    const relatedEntitiesMap = new Map<keyof EditableRow, typeof financialEntities>();
    relatedEntitiesMap.set('financial_entity_id', financialEntities);

    // Apply pasted data to rows
    const createEmptyRow = (): EditableRow => ({
      description: '',
      provider_id_code: '',
      provider_id_type: 'LEI',
      additional_id_code: '',
      additional_id_type: '',
      legal_name: '',
      name_latin: '',
      person_type: PERSON_TYPE_OPTIONS[0],
      headquarters_country: '',
      currency_code: '',
      annual_expense: '',
      parent_id_code: '',
      parent_id_type: '',
      financial_entity_id: financialEntities[0]?.id ?? '',
      isNew: true,
      touched: true, // Pasted rows are considered touched
    });

    setEditableRows(prev => {
      const updated = applyPastedData(
        prev,
        normalizedRows,
        startRowIndex,
        startFieldIndex,
        fieldOrder,
        relatedEntitiesMap,
        createEmptyRow
      );
      // Mark affected rows as touched
      return updated.map((row, idx) => {
        if (idx >= startRowIndex && idx < startRowIndex + normalizedRows.length && row.isNew) {
          return { ...row, touched: true };
        }
        return row;
      });
    });

    // Auto-save pasted rows after a short delay
    setTimeout(() => {
      parsedRows.forEach((_, rowOffset) => {
        const targetRowIndex = startRowIndex + rowOffset;
        if (targetRowIndex < editableRows.length) {
          saveRow(editableRows[targetRowIndex], targetRowIndex);
        }
      });
    }, 300);
  }, [editableRows, financialEntities, saveRow, fieldOrder]);

  // Helper to get provider ID type options based on person type
  const getProviderIdTypeOptions = (personType: string): string[] => {
    const isLegalPerson = personType?.includes("Legal person");
    if (isLegalPerson) {
      return [...PROVIDER_ID_TYPE_BASE_OPTIONS];
    }
    // For individuals, show base types plus example pattern for national codes
    return [
      ...PROVIDER_ID_TYPE_BASE_OPTIONS,
      '-- National Codes --',
      'US_CRN',
      'DE_VAT',
      'FR_NIN',
      '(Country_Type)',
    ];
  };

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {financialEntities.length === 0 && (
        <Alert severity="warning">
          No Financial Entities found. Please create financial entities in the <strong>Financial Entities</strong> panel (B_01.02) before creating ICT third-party providers.
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6">ICT Third-Party Providers (B_05.01)</Typography>
            {loading && <CircularProgress size={20} />}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click cells to edit. Tab to navigate. Changes auto-save when moving to another row. Paste from Excel/spreadsheet supported.
            <br />
            <strong>Required fields:</strong> Provider ID Code, ID Type, Additional ID Code/Type, Legal Name, Name (Latin), Person Type, HQ Country, Financial Entity
            <br />
            <strong>Conditional:</strong> Currency Code (if Annual Expense provided), Parent ID Code/Type (both or neither)
          </Typography>
          <TableContainer sx={{ border: '1px solid', borderColor: 'divider', maxHeight: '70vh', overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 150 }}>Provider ID Code*</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 120 }}>ID Type*</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 180 }}>Legal Name*</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 180 }}>Name (Latin)*</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 200 }}>Person Type*</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 120 }}>HQ Country*</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 150 }}>Add'l ID Code*</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 120 }}>Add'l ID Type*</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 120 }}>Annual Expense</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 100 }}>Currency</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 150 }}>Parent ID Code</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 120 }}>Parent ID Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 180 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 180 }}>Financial Entity*</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', width: 60 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {editableRows.map((row, index) => {
                  const feName = financialEntities.find(fe => fe.id === row.financial_entity_id)?.name || '';
                  const isFocused = focusedRowIndex === index;
                  const isEvenRow = index % 2 === 0;

                  return (
                    <TableRow
                      key={row.id ?? `new-${index}`}
                      sx={{
                        bgcolor: getRowBackgroundColor(isFocused, row.isNew || false, isEvenRow),
                        '&:hover': {
                          bgcolor: getRowHoverColor(isFocused),
                        },
                        transition: 'background-color 0.15s ease',
                      }}
                    >
                      {/* Provider ID Code */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'provider_id_code' ? (
                          <TextField
                            value={row.provider_id_code}
                            onChange={(e) => updateRow(index, 'provider_id_code', e.target.value.toUpperCase())}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'provider_id_code')}
                            onPaste={(e) => handlePaste(e, index, 'provider_id_code')}
                            size="small"
                            fullWidth
                            variant="standard"
                            placeholder="LEI/EUID/Code"
                            InputProps={{ disableUnderline: true }}
                            sx={{ '& input': { p: 1 } }}
                            autoFocus
                          />
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'provider_id_code' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'text',
                              userSelect: 'text',
                              background: getCellBackgroundColor(row, 'provider_id_code'),
                              transition: 'background 0.15s ease',
                            }}
                          >
                            {row.provider_id_code || (row.isNew ? <span style={{ color: '#999' }}>LEI/EUID/Code...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Provider ID Type */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'provider_id_type' ? (
                          <FormControl fullWidth size="small" variant="standard">
                            <Select
                              value={row.provider_id_type}
                              onChange={(e: SelectChangeEvent<string>) => {
                                const value = e.target.value;
                                // Skip separator and example entries
                                if (!value.startsWith('--') && !value.startsWith('(')) {
                                  updateRow(index, 'provider_id_type', value);
                                }
                              }}
                              onBlur={() => {
                                handleRowBlur();
                                setEditingCell(null);
                              }}
                              onKeyDown={(e) => handleTabNavigation(e, index, 'provider_id_type')}
                              displayEmpty
                              disableUnderline
                              sx={{ '& .MuiSelect-select': { p: 1 } }}
                              autoFocus
                            >
                              {getProviderIdTypeOptions(row.person_type).map(type => (
                                <MenuItem
                                  key={type}
                                  value={type}
                                  disabled={type.startsWith('--') || type.startsWith('(')}
                                  sx={type.startsWith('--') ? { fontWeight: 'bold', color: 'text.secondary' } : {}}
                                >
                                  {type}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'provider_id_type' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'pointer',
                              userSelect: 'text',
                              background: getCellBackgroundColor(row, 'provider_id_type'),
                              transition: 'background 0.15s ease',
                            }}
                          >
                            {row.provider_id_type || (row.isNew ? <span style={{ color: '#999' }}>Select...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Legal Name */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'legal_name' ? (
                          <TextField
                            value={row.legal_name}
                            onChange={(e) => updateRow(index, 'legal_name', e.target.value)}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'legal_name')}
                            onPaste={(e) => handlePaste(e, index, 'legal_name')}
                            size="small"
                            fullWidth
                            variant="standard"
                            placeholder="Legal name"
                            InputProps={{ disableUnderline: true }}
                            sx={{ '& input': { p: 1 } }}
                            autoFocus
                          />
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'legal_name' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'text',
                              userSelect: 'text',
                              background: getCellBackgroundColor(row, 'legal_name'),
                              transition: 'background 0.15s ease',
                            }}
                          >
                            {row.legal_name || (row.isNew ? <span style={{ color: '#999' }}>Legal name...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Name Latin */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'name_latin' ? (
                          <TextField
                            value={row.name_latin}
                            onChange={(e) => updateRow(index, 'name_latin', e.target.value)}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'name_latin')}
                            onPaste={(e) => handlePaste(e, index, 'name_latin')}
                            size="small"
                            fullWidth
                            variant="standard"
                            placeholder="Name in Latin alphabet"
                            InputProps={{ disableUnderline: true }}
                            sx={{ '& input': { p: 1 } }}
                            autoFocus
                          />
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'name_latin' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'text',
                              userSelect: 'text',
                              background: getCellBackgroundColor(row, 'name_latin'),
                              transition: 'background 0.15s ease',
                            }}
                          >
                            {row.name_latin || (row.isNew ? <span style={{ color: '#999' }}>Name (Latin)...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Person Type */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'person_type' ? (
                          <FormControl fullWidth size="small" variant="standard">
                            <Select
                              value={row.person_type}
                              onChange={(e: SelectChangeEvent<string>) => {
                                updateRow(index, 'person_type', e.target.value);
                              }}
                              onBlur={() => {
                                handleRowBlur();
                                setEditingCell(null);
                              }}
                              onKeyDown={(e) => handleTabNavigation(e, index, 'person_type')}
                              displayEmpty
                              disableUnderline
                              sx={{ '& .MuiSelect-select': { p: 1 } }}
                              autoFocus
                            >
                              {PERSON_TYPE_OPTIONS.map(type => (
                                <MenuItem key={type} value={type}>{type}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'person_type' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'pointer',
                              userSelect: 'text',
                              background: getCellBackgroundColor(row, 'person_type'),
                              transition: 'background 0.15s ease',
                            }}
                          >
                            {row.person_type || (row.isNew ? <span style={{ color: '#999' }}>Select...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Headquarters Country */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'headquarters_country' ? (
                          <Autocomplete
                            value={COUNTRY_OPTIONS.find(c => c.code === row.headquarters_country) || null}
                            onChange={(_, newValue) => {
                              updateRow(index, 'headquarters_country', newValue?.code || '');
                            }}
                            options={COUNTRY_OPTIONS}
                            getOptionLabel={(option) => `${option.flag} ${option.name}`}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Tab') {
                                handleTabNavigation(e, index, 'headquarters_country');
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="standard"
                                placeholder="Select country"
                                InputProps={{
                                  ...params.InputProps,
                                  disableUnderline: true,
                                }}
                                sx={{ '& input': { p: 1 } }}
                                autoFocus
                              />
                            )}
                            size="small"
                            fullWidth
                          />
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'headquarters_country' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'pointer',
                              userSelect: 'text',
                              background: getCellBackgroundColor(row, 'headquarters_country'),
                              transition: 'background 0.15s ease',
                            }}
                          >
                            {COUNTRY_OPTIONS.find(c => c.code === row.headquarters_country)?.name || (row.isNew ? <span style={{ color: '#999' }}>Select...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Additional ID Code */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'additional_id_code' ? (
                          <TextField
                            value={row.additional_id_code}
                            onChange={(e) => updateRow(index, 'additional_id_code', e.target.value)}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'additional_id_code')}
                            onPaste={(e) => handlePaste(e, index, 'additional_id_code')}
                            size="small"
                            fullWidth
                            variant="standard"
                            placeholder="Additional ID"
                            InputProps={{ disableUnderline: true }}
                            sx={{ '& input': { p: 1 } }}
                            autoFocus
                          />
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'additional_id_code' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'text',
                              userSelect: 'text',
                              background: getCellBackgroundColor(row, 'additional_id_code'),
                              transition: 'background 0.15s ease',
                            }}
                          >
                            {row.additional_id_code || (row.isNew ? <span style={{ color: '#999' }}>Add'l ID...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Additional ID Type */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'additional_id_type' ? (
                          <FormControl fullWidth size="small" variant="standard">
                            <Select
                              value={row.additional_id_type}
                              onChange={(e: SelectChangeEvent<string>) => {
                                updateRow(index, 'additional_id_type', e.target.value);
                              }}
                              onBlur={() => {
                                handleRowBlur();
                                setEditingCell(null);
                              }}
                              onKeyDown={(e) => handleTabNavigation(e, index, 'additional_id_type')}
                              displayEmpty
                              disableUnderline
                              sx={{ '& .MuiSelect-select': { p: 1 } }}
                              autoFocus
                            >
                              {row.isNew && <MenuItem value=""><em>Select...</em></MenuItem>}
                              {[...PROVIDER_ID_TYPE_BASE_OPTIONS, ...NATIONAL_ID_TYPES].map(type => (
                                <MenuItem key={type} value={type}>{type}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'additional_id_type' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'pointer',
                              userSelect: 'text',
                              background: getCellBackgroundColor(row, 'additional_id_type'),
                              transition: 'background 0.15s ease',
                            }}
                          >
                            {row.additional_id_type || (row.isNew ? <span style={{ color: '#999' }}>Select...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Annual Expense */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'annual_expense' ? (
                          <TextField
                            value={row.annual_expense}
                            onChange={(e) => updateRow(index, 'annual_expense', e.target.value)}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'annual_expense')}
                            onPaste={(e) => handlePaste(e, index, 'annual_expense')}
                            size="small"
                            fullWidth
                            variant="standard"
                            type="number"
                            placeholder="Amount"
                            InputProps={{ disableUnderline: true }}
                            sx={{ '& input': { p: 1 } }}
                            autoFocus
                          />
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'annual_expense' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'text',
                              userSelect: 'text',
                              background: getCellBackgroundColor(row, 'annual_expense'),
                              transition: 'background 0.15s ease',
                            }}
                          >
                            {row.annual_expense || (row.isNew ? <span style={{ color: '#999' }}>Amount...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Currency Code */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'currency_code' ? (
                          <Autocomplete
                            value={CURRENCY_OPTIONS.find(c => c.code === row.currency_code) || null}
                            onChange={(_, newValue) => {
                              updateRow(index, 'currency_code', newValue?.code || '');
                            }}
                            options={CURRENCY_OPTIONS}
                            getOptionLabel={(option) => `${option.code} - ${option.symbol}`}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Tab') {
                                handleTabNavigation(e, index, 'currency_code');
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="standard"
                                placeholder="Currency"
                                InputProps={{
                                  ...params.InputProps,
                                  disableUnderline: true,
                                }}
                                sx={{ '& input': { p: 1 } }}
                                autoFocus
                              />
                            )}
                            size="small"
                            fullWidth
                          />
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'currency_code' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'pointer',
                              userSelect: 'text',
                              background: getCellBackgroundColor(row, 'currency_code'),
                              transition: 'background 0.15s ease',
                            }}
                          >
                            {row.currency_code || (row.isNew ? <span style={{ color: '#999' }}>Currency...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Parent ID Code */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'parent_id_code' ? (
                          <TextField
                            value={row.parent_id_code}
                            onChange={(e) => updateRow(index, 'parent_id_code', e.target.value)}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'parent_id_code')}
                            onPaste={(e) => handlePaste(e, index, 'parent_id_code')}
                            size="small"
                            fullWidth
                            variant="standard"
                            placeholder="Parent ID"
                            InputProps={{ disableUnderline: true }}
                            sx={{ '& input': { p: 1 } }}
                            autoFocus
                          />
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'parent_id_code' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'text',
                              userSelect: 'text',
                              background: getCellBackgroundColor(row, 'parent_id_code'),
                              transition: 'background 0.15s ease',
                            }}
                          >
                            {row.parent_id_code || (row.isNew ? <span style={{ color: '#999' }}>Parent ID...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Parent ID Type */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'parent_id_type' ? (
                          <FormControl fullWidth size="small" variant="standard">
                            <Select
                              value={row.parent_id_type}
                              onChange={(e: SelectChangeEvent<string>) => {
                                updateRow(index, 'parent_id_type', e.target.value);
                              }}
                              onBlur={() => {
                                handleRowBlur();
                                setEditingCell(null);
                              }}
                              onKeyDown={(e) => handleTabNavigation(e, index, 'parent_id_type')}
                              displayEmpty
                              disableUnderline
                              sx={{ '& .MuiSelect-select': { p: 1 } }}
                              autoFocus
                            >
                              {row.isNew && <MenuItem value=""><em>Select...</em></MenuItem>}
                              {PROVIDER_ID_TYPE_BASE_OPTIONS.map(type => (
                                <MenuItem key={type} value={type}>{type}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'parent_id_type' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'pointer',
                              userSelect: 'text',
                              background: getCellBackgroundColor(row, 'parent_id_type'),
                              transition: 'background 0.15s ease',
                            }}
                          >
                            {row.parent_id_type || (row.isNew ? <span style={{ color: '#999' }}>Parent type...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Description */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'description' ? (
                          <TextField
                            value={row.description}
                            onChange={(e) => updateRow(index, 'description', e.target.value)}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'description')}
                            onPaste={(e) => handlePaste(e, index, 'description')}
                            size="small"
                            fullWidth
                            variant="standard"
                            placeholder="Optional notes"
                            InputProps={{ disableUnderline: true }}
                            sx={{ '& input': { p: 1 } }}
                            autoFocus
                          />
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'description' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'text',
                              userSelect: 'text',
                              background: getCellBackgroundColor(row, 'description'),
                              transition: 'background 0.15s ease',
                            }}
                          >
                            {row.description || (row.isNew ? <span style={{ color: '#999' }}>Notes...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Financial Entity */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'financial_entity_id' ? (
                          <FormControl fullWidth size="small" variant="standard">
                            <Select
                              value={row.financial_entity_id === '' ? '' : String(row.financial_entity_id)}
                              onChange={(e: SelectChangeEvent<string>) => {
                                updateRow(index, 'financial_entity_id', Number(e.target.value));
                              }}
                              onBlur={() => {
                                handleRowBlur();
                                setEditingCell(null);
                              }}
                              onKeyDown={(e) => handleTabNavigation(e, index, 'financial_entity_id')}
                              displayEmpty
                              disableUnderline
                              sx={{ '& .MuiSelect-select': { p: 1 } }}
                              autoFocus
                            >
                              {row.isNew && <MenuItem value=""><em>Select entity...</em></MenuItem>}
                              {financialEntities.map(fe => (
                                <MenuItem key={fe.id} value={fe.id}>{fe.name}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'financial_entity_id' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'pointer',
                              userSelect: 'text',
                              background: getCellBackgroundColor(row, 'financial_entity_id'),
                              transition: 'background 0.15s ease',
                            }}
                          >
                            {feName || (row.isNew ? <span style={{ color: '#999' }}>Select entity...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right">
                        {!row.isNew && (
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(row.id)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Stack>
  );
};
